import { createRoute } from "@tanstack/react-router";
import { FormEvent, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import type { UserRole } from "../auth/AuthContext";
import { useAuth } from "../auth/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useCreateUser, useResetUserPassword, useUpdateUser, useUsers } from "../queries";
import { rootRoute } from "./__root";

const roles: UserRole[] = ["owner", "gm", "sales", "tech", "viewer"];

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/users",
  component: UsersScreen,
});

function UsersScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm"]}>
      <UsersContent />
    </ProtectedRoute>
  );
}

function UsersContent() {
  const { user } = useAuth();
  const users = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const resetPassword = useResetUserPassword();
  const [showForm, setShowForm] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      username: String(form.get("username") || ""),
      full_name: String(form.get("full_name") || ""),
      email: String(form.get("email") || "") || null,
      role: String(form.get("role") || "viewer") as UserRole,
    };
    const result = await createUser.mutateAsync(payload);
    setTempPassword(result.temporary_password);
    setShowForm(false);
    event.currentTarget.reset();
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <Badge variant="slate">Admin</Badge>
          <h1 className="mt-3 text-2xl font-semibold text-slate-950">Users</h1>
          <p className="mt-1 text-sm text-slate-600">Create dashboard users and manage roles.</p>
        </div>
        <Button onClick={() => setShowForm((value) => !value)}>{showForm ? "Close" : "+ Add user"}</Button>
      </div>

      {tempPassword ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm font-semibold text-amber-900">Temporary password shown once</div>
              <code className="mt-1 block text-lg font-semibold text-amber-950">{tempPassword}</code>
            </div>
            <Button variant="secondary" onClick={() => setTempPassword(null)}>I transmitted it</Button>
          </CardContent>
        </Card>
      ) : null}

      {showForm ? (
        <Card>
          <CardHeader>
            <CardTitle>Add user</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-5">
              <input name="username" className="rounded-md border px-3 py-2 text-sm" placeholder="username" required />
              <input name="full_name" className="rounded-md border px-3 py-2 text-sm" placeholder="Full name" required />
              <input name="email" className="rounded-md border px-3 py-2 text-sm" placeholder="email optional" type="email" />
              <select name="role" className="rounded-md border px-3 py-2 text-sm" defaultValue="viewer">
                {roles.filter((role) => user?.role === "owner" || role !== "owner").map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <Button disabled={createUser.isPending} type="submit">Create</Button>
            </form>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Tenant users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="py-2">Username</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(users.data?.users || []).map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-3 font-medium text-slate-950">{row.username}</td>
                    <td>{row.full_name || "-"}</td>
                    <td>{row.email || "-"}</td>
                    <td><Badge variant="slate">{row.role}</Badge></td>
                    <td>{row.is_active ? "Active" : "Deactivated"}</td>
                    <td className="space-x-2 text-right">
                      <Button
                        variant="secondary"
                        onClick={async () => {
                          const result = await resetPassword.mutateAsync(row.id);
                          setTempPassword(result.temporary_password);
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => updateUser.mutate({ id: row.id, patch: { is_active: !row.is_active } })}
                      >
                        {row.is_active ? "Deactivate" : "Reactivate"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
